import { useState, useMemo } from 'react';
import { ThProps } from '@patternfly/react-table';

export type SortDirection = 'asc' | 'desc';

/**
 * A function that extracts sortable primitive values from a row item.
 * The returned array index corresponds to the column index used in the table.
 *
 * @example
 * const getRowValues = (ns: Namespace) => [ns.name, ns.creator, ns.team, ns.date.getTime(), ns.status];
 */
export type RowValueExtractor<T> = (item: T) => (string | number | null | undefined)[];

export interface UseSortableDataOptions {
  initialSortIndex?: number;
  initialSortDirection?: SortDirection;
}

export interface UseSortableDataResult<T> {
  /** Sorted copy of the input data — never mutates the original array. */
  sortedData: T[];
  /**
   * Returns PatternFly-compatible sort props for a `<Th sort={...}>` header cell.
   * Pass the zero-based column index that matches the position in `getRowValues`.
   */
  getSortParams: (columnIndex: number) => ThProps['sort'];
  activeSortIndex: number;
  activeSortDirection: SortDirection;
}

/**
 * Reusable hook for client-side column sorting in PatternFly tables.
 *
 * Generic over `T` (the row data type), so it works with any table in the app.
 *
 * @param data            - The source array to sort (not mutated).
 * @param getRowValues    - Extracts an ordered list of sortable values from each row.
 * @param options         - Optional initial sort column/direction (defaults to column 0 asc).
 *
 * @example
 * const { sortedData, getSortParams } = useSortableData(
 *   namespaces,
 *   (ns) => [ns.name, ns.creator, ns.team, ns.date.getTime(), ns.status],
 * );
 */
export function useSortableData<T>(
  data: T[],
  getRowValues: RowValueExtractor<T>,
  options: UseSortableDataOptions = {}
): UseSortableDataResult<T> {
  const { initialSortIndex = 0, initialSortDirection = 'asc' } = options;

  const [activeSortIndex, setActiveSortIndex] = useState<number>(initialSortIndex);
  const [activeSortDirection, setActiveSortDirection] = useState<SortDirection>(initialSortDirection);

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const aValue = getRowValues(a)[activeSortIndex];
      const bValue = getRowValues(b)[activeSortIndex];
      const aMissing = aValue == null;
      const bMissing = bValue == null;

      // Always place missing values after real values, regardless of sort direction.
      if (aMissing && bMissing) {
        return 0;
      }
      if (aMissing) {
        return 1;
      }
      if (bMissing) {
        return -1;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return activeSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const cmp = String(aValue).localeCompare(String(bValue), undefined, { sensitivity: 'base' });
      return activeSortDirection === 'asc' ? cmp : -cmp;
    });
  }, [data, activeSortIndex, activeSortDirection, getRowValues]);

  const getSortParams = (columnIndex: number): ThProps['sort'] => ({
    sortBy: {
      index: activeSortIndex,
      direction: activeSortDirection,
      defaultDirection: 'asc',
    },
    onSort: (_event, index, direction) => {
      setActiveSortIndex(index);
      if (direction === 'asc' || direction === 'desc') {
        setActiveSortDirection(direction);
      }
    },
    columnIndex,
  });

  return { sortedData, getSortParams, activeSortIndex, activeSortDirection };
}
